/**
 * The membership card as it goes on plastic.
 *
 * Ported from `legacy/frontendx/src/apps/mrm/components/MrmRowDetailView/
 * MrmMembershipCardPrint.tsx`, laid out to the Gold and Business card designs:
 * 89.6mm × 57mm (4mm clear, 12mm stripe, 37mm body, 4mm footer). The mockups
 * mark the heights; the width is their proportion, 1.57 : 1, at that height.
 * The photograph and the QR sit at the millimetres the designs give.
 *
 * Nothing here is themed and nothing scales with the app's type: the card is a
 * physical object, the same card in dark mode as in light, and a member whose
 * screen is in Filipino is handed the same English card as everybody else — the
 * words below are printed on the stock, not shown on a screen.
 */

import { QRCode } from "antd";

import type { Member } from "@/pages/MembersPage";

/**
 * The words on the back of each card, as the two designs print them. They are
 * not the same card with a different label: the Gold and Business stock were
 * drawn separately, and the wording, the phone line and the footer differ.
 */
const GOLD_TEXT = {
  terms: [
    "This Membership Card is NON-TRANSFERABLE & valid at all S&R branches.",
    "Please present this at the entrance and upon purchase.",
  ],
  assistance: "For assistance, call (02) 8888-0640",
  website: "www.snrshopping.com",
};
const BUSINESS_TEXT = {
  terms: [
    "This Membership card is NON-TRANSFERRABLE. Tampering of this card will invalidate it.",
    "Please present this card at the entrance and at the cashier upon payment.",
  ],
  assistance: "For assistance, please call: (02) 8888-0433",
  website: "visit our website at www.snrshopping.com",
};

/** Digits in fours, as they are engraved: 427168965000 → 4271 6896 5000. */
function inFours(value: string | null | undefined): string {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length < 8) return String(value ?? "").trim();
  const groups: string[] = [];
  for (let at = 0; at < digits.length; at += 4) groups.push(digits.slice(at, at + 4));
  return groups.join(" ");
}

/**
 * The account number the way the card design writes it: a ten-digit account
 * is 1-4-4-1 (2006233525 → 2 0062 3352 5). Anything else falls back to fours.
 */
function accountGroups(value: string | null | undefined): string {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length !== 10) return inFours(value);
  return [digits.slice(0, 1), digits.slice(1, 5), digits.slice(5, 9), digits.slice(9)].join(
    " ",
  );
}

/** FIRST M. LAST SUFFIX — the middle name is an initial on a card this size. */
function cardName(member: Member): string {
  const middle = member.middle_name ? `${member.middle_name.charAt(0).toUpperCase()}.` : "";
  return [member.first_name, middle, member.last_name, member.suffix]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();
}

/**
 * What a saved copy of the card is called. Browsers name "Save as PDF" after
 * the page title, so every card came out as the app's name and each save asked
 * to overwrite the last. The member's name and card number make it theirs:
 * "JUAN C. DELA CRUZ - 427168965000". Characters a file name cannot hold are
 * dropped.
 */
export function cardFileName(member: Member): string {
  const number = String(member.card_number || member.account_number || member.id || "").replace(
    /\D/g,
    "",
  );
  return [cardName(member), number]
    .filter(Boolean)
    .join(" - ")
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * The number the gate scans. antd draws it as SVG: a canvas is a bitmap the
 * printer has to guess at, and the code has to come out sharp at under 10mm.
 */
function CardQr({ member, mm }: { member: Member; mm: number }) {
  const px = Math.round(mm * (96 / 25.4));
  return (
    <div className="shrink-0 bg-white" style={{ width: `${mm}mm`, height: `${mm}mm` }}>
      <QRCode
        type="svg"
        value={member.account_number || member.card_number || "SNR_MEMBER"}
        size={px}
        bordered={false}
        color="#000000"
        bgColor="#ffffff"
        style={{ padding: 0, width: `${mm}mm`, height: `${mm}mm` }}
      />
    </div>
  );
}

/*
 * Both designs share the same bands top to bottom — 4mm clear, the 12mm
 * magnetic stripe, the 37mm printed body and the 4mm blue footer, 57mm in all —
 * and differ only inside the body.
 */
const CARD =
  "relative flex h-[57mm] w-[89.6mm] flex-col overflow-hidden rounded-[3.5mm] bg-white text-[#111]";
// The app's body text is tracked in a hair; the card is set as drawn.
const FONT = {
  fontFamily: '"Card Condensed", "Arial Narrow", Arial, sans-serif',
  letterSpacing: 0,
};

function Stripe() {
  return (
    <>
      <div className="h-[4mm] shrink-0" />
      <div className="h-[12mm] shrink-0 bg-[#3a3a3a]" />
    </>
  );
}

/**
 * The card's outline, drawn over the bands rather than as a border on the
 * card: a border sits inside the 57mm and would shave the 4mm top band and
 * the 4mm footer.
 */
function Outline() {
  return (
    <div className="pointer-events-none absolute inset-0 rounded-[3.5mm] border border-[#222]" />
  );
}

function Footer({ text, bold }: { text: string; bold: boolean }) {
  return (
    <div className="flex h-[4mm] shrink-0 items-center justify-center bg-[#1f3d78]">
      <span
        className={`text-[3.1mm] leading-none whitespace-nowrap text-white ${bold ? "font-bold" : ""}`}
      >
        {text}
      </span>
    </div>
  );
}

function Terms({
  lines,
  assistance,
  boldAssistance,
  className,
}: {
  lines: string[];
  assistance: string;
  boldAssistance: boolean;
  /** Size and line pitch, which the two designs set differently. */
  className: string;
}) {
  return (
    <div className={`whitespace-nowrap ${className}`}>
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
      <div className={boldAssistance ? "font-bold" : ""}>{assistance}</div>
    </div>
  );
}

/** Gold: photo flush to the card's right edge, QR 5mm left of it. */
function GoldCard({ member, label, since }: CardProps) {
  const numbers = [accountGroups(member.account_number), inFours(member.card_number)]
    .filter(Boolean)
    .join(" | ");
  // Each line is placed on its own: the tops below put the cap height where
  // the Gold mockup has it (name 3.0mm into the body, numbers 10.3mm, "Since"
  // 14.0mm, terms 28.0mm), and the sizes give the widths it has.
  return (
    <div className={CARD} style={FONT}>
      <Stripe />
      <div className="relative h-[37mm] shrink-0">
        <div className="absolute top-[0.6mm] right-0 h-[22.6mm] w-[25.5mm] overflow-hidden">
          {member.profile_image ? (
            <img
              src={member.profile_image}
              alt=""
              className="size-full object-cover object-top"
            />
          ) : null}
        </div>
        <div className="absolute top-[2.45mm] right-[26.7mm] left-[2mm] truncate text-[4.4mm] leading-none font-bold uppercase">
          {cardName(member)}
        </div>
        <div className="absolute top-[9.8mm] left-[2mm] text-[4mm] leading-none font-bold whitespace-nowrap">
          {numbers}
        </div>
        <div className="absolute top-[13.5mm] left-[2mm] text-[3.58mm] leading-none whitespace-nowrap">
          <span>{label} Since:</span> <span>{since}</span>
        </div>
        <div className="absolute top-[14mm] right-[26.7mm]">
          <CardQr member={member} mm={9.1} />
        </div>
        <div className="absolute top-[27.55mm] left-[2mm]">
          <Terms
            lines={GOLD_TEXT.terms}
            assistance={GOLD_TEXT.assistance}
            boldAssistance={false}
            className="text-[2.28mm] leading-[2.55mm]"
          />
        </div>
      </div>
      <Footer text={GOLD_TEXT.website} bold />
      <Outline />
    </div>
  );
}

/** Business: framed 25×24mm photo, trading name under the member's own. */
function BusinessCard({ member, label, since }: CardProps) {
  const numbers = [accountGroups(member.account_number), inFours(member.card_number)]
    .filter(Boolean)
    .join(" | ");
  // Placed line by line to the Business mockup's cap heights (name 2.45mm
  // into the body, trading name 6.2mm, numbers 9.7mm, "Since" 14.0mm), with
  // the QR's foot level with the photo's and the terms 4mm under both.
  return (
    <div className={CARD} style={FONT}>
      <Stripe />
      <div className="relative h-[37mm] shrink-0">
        <div className="absolute top-[0.6mm] right-[2.2mm] h-[24mm] w-[25mm] overflow-hidden border border-[#222] bg-[#f2f2f2]">
          {member.profile_image ? (
            <img
              src={member.profile_image}
              alt=""
              className="size-full object-cover object-top"
            />
          ) : null}
        </div>
        <div className="absolute top-[1.9mm] right-[28mm] left-[2mm] truncate text-[4.38mm] leading-none font-bold uppercase">
          {cardName(member)}
        </div>
        {/* The trading name under the member's own — the name the cashier
            checks the card against (FSD §12). Only where there is one. */}
        {member.business_name ? (
          <div className="absolute top-[5.8mm] right-[28mm] left-[2mm] truncate text-[3.1mm] leading-none">
            {member.business_name}
          </div>
        ) : null}
        <div className="absolute top-[9.2mm] left-[2mm] text-[3.88mm] leading-none font-bold whitespace-nowrap">
          {numbers}
        </div>
        <div className="absolute top-[13.6mm] left-[2mm] text-[2.88mm] leading-none whitespace-nowrap">
          <span>{label} Since:</span> <span>{since}</span>
        </div>
        <div className="absolute top-[15.6mm] right-[30.2mm]">
          <CardQr member={member} mm={9} />
        </div>
        <div className="absolute top-[28.05mm] left-[2mm]">
          <Terms
            lines={BUSINESS_TEXT.terms}
            assistance={BUSINESS_TEXT.assistance}
            boldAssistance
            className="text-[2.27mm] leading-[2.65mm]"
          />
        </div>
      </div>
      <Footer text={BUSINESS_TEXT.website} bold={false} />
      <Outline />
    </div>
  );
}

type CardProps = {
  member: Member;
  /** "Gold Member" — the membership as the banner names it. */
  label: string;
  /** The day they became one, written the way the screens write dates. */
  since: string;
};

/**
 * Business memberships get the Business design; every other membership
 * (Gold, and the types without a design of their own) gets the Gold one.
 */
export function MembershipCardPrint(props: CardProps) {
  return props.member.membership_type === "business" ? (
    <BusinessCard {...props} />
  ) : (
    <GoldCard {...props} />
  );
}
